// Customer-facing setup instructions for the nightly sync, in one place so
// the dialog and the "copy instructions for IT" text say the same thing.
// Pure: unit-tested, no React.

export interface SyncSetupValues {
  /** The API origin the script posts to (-PostTo). */
  apiOrigin: string;
  tenantId: string;
  /** Absolute URL of export-teams-callflows.ps1 on this site. */
  scriptUrl: string;
  /** Absolute URL of register-quickdial-sync-app.ps1 on this site. */
  registerScriptUrl: string;
  appName?: string;
}

export const SCRIPT_PATH = 'C:\\QuickDial\\export-teams-callflows.ps1';
export const PFX_PATH = 'C:\\QuickDial\\quickdial-sync.pfx';
export const SECRET_ENV = 'QUICKDIAL_INGEST_SECRET';
export const CERT_PASSWORD_ENV = 'QUICKDIAL_CERT_PASSWORD';

/**
 * The Arguments field of the scheduled task. Certificate sign-in only, and
 * there is no -Credential variant of it: the task starts this as
 * `pwsh -File <script> ...`, and -File passes everything after the script path
 * as literal text, so "-Credential (Get-Credential)" would reach the script as
 * those characters rather than as a prompt -- and a task set to "Run whether
 * user is logged on or not" has no screen to prompt on anyway. Option B in the
 * sign-in step explains where a service account can still be used.
 *
 * The certificate is passed as a FILE rather than a thumbprint. A thumbprint is
 * looked up in the user store of the account the task runs as, and that store
 * is the one place an administrator cannot fill in from outside -- it means
 * signing in as that account once, which a task account is usually forbidden to
 * do. Anyone who already has the certificate in that store can swap in
 * -CertificateThumbprint instead; the script accepts either.
 */
export function syncArguments(v: SyncSetupValues): string {
  return `-NoProfile -File "${SCRIPT_PATH}" -PostTo ${v.apiOrigin} -TenantId ${v.tenantId}`
    + ` -ApplicationId <application-id> -CertificatePath "${PFX_PATH}"`;
}

export function syncCommand(v: SyncSetupValues): string {
  return `pwsh ${syncArguments(v)}`;
}

/**
 * One piece of a setup step. The dialog gives each kind its own layout and the
 * plain-text export flattens them, so the two can never drift apart.
 */
export type StepBlock =
  /** A paragraph of explanation. */
  | { kind: 'p'; text: string }
  /** A command to run, shown with a copy button. */
  | { kind: 'cmd'; command: string }
  /** A URL to open or download. */
  | { kind: 'link'; text: string; href: string }
  /** One of several ways to do the step — app registration vs service account, Windows vs Azure. */
  | { kind: 'option'; label: string; badge?: string; text: string; items?: string[]; commands?: string[] }
  /** Key/value rows, e.g. the exit codes. */
  | { kind: 'rows'; label?: string; items: Array<[key: string, meaning: string]> };

export interface SetupStep {
  title: string;
  blocks: StepBlock[];
}

/** What happens to each phone menu on a run — shown as a table in the dialog. */
export const SYNC_OUTCOMES: Array<[situation: string, result: string]> = [
  ['The Teams configuration changed and everything in it can be represented', 'The phone menu is updated, with an entry in the audit log'],
  ['Nothing changed since the last run', 'Only the "synced from Teams" timestamp moves'],
  ['Something cannot be represented (for example a menu option that points at a target Quick Dial does not know)', 'The proposal is held for your review below; nothing is written until you save it'],
  ['A menu that is only reachable from another menu', 'Skipped: it is already shown inside its parent menu'],
  ['An auto attendant that no longer exists in Teams', 'Reported as "no longer in Teams"; the location is kept, never deleted'],
  ['Recordings (greetings, hold music) Quick Dial does not have yet', 'The task uploads them in the same run'],
];

export const SYNC_NEVER: string[] = [
  'changes anything in Teams: the script only uses read-only commands',
  'deletes a location',
  'overwrites the names, viewer groups, NL/EN labels or recordings your admins chose in Quick Dial',
  'applies a change it could not represent fully',
];

export function syncSetupSteps(v: SyncSetupValues): SetupStep[] {
  return [
    {
      title: 'Choose where the task runs',
      blocks: [
        { kind: 'p', text: 'A Windows server or PC that is switched on at night, or an Azure Automation account. It needs internet access to Microsoft 365 and to the Quick Dial API below.' },
      ],
    },
    {
      title: 'Install PowerShell 7 and the Microsoft Teams module',
      blocks: [
        { kind: 'p', text: 'Both come from Microsoft. Run these once on that machine:' },
        { kind: 'cmd', command: 'winget install Microsoft.PowerShell' },
        { kind: 'cmd', command: 'pwsh -Command "Install-Module MicrosoftTeams -Scope CurrentUser"' },
      ],
    },
    {
      title: 'Download the export script',
      blocks: [
        { kind: 'p', text: `Save it as ${SCRIPT_PATH}. It only uses Get- and Export- commands from Microsoft's module and changes nothing in Teams; you can open it in Notepad to check.` },
        { kind: 'link', text: 'Download export-teams-callflows.ps1', href: v.scriptUrl },
      ],
    },
    {
      title: 'Choose how the task signs in to Teams',
      blocks: [
        {
          kind: 'option',
          label: 'Option A — app registration with a certificate',
          badge: 'recommended',
          text: 'No passwords and no MFA prompts. One script does the whole registration: run it once from your own workstation as a Global Administrator and it creates the app, its certificate, the single Microsoft Graph permission with admin consent, and the Teams Administrator role — then prints the command line for the task. It changes nothing in Teams.',
          items: [
            `Download the script below and run it. It writes ${PFX_PATH} — the certificate and its private key — and prints the Application (client) ID and the .pfx password. Keep that password; it is shown once.`,
            'Copy the .pfx to the machine that runs the task and give the account the task runs as read access to that file. That account never has to sign in anywhere, so this works even where interactive logon is denied for it.',
            'Set a reminder for the expiry date it prints (two years by default). Re-run the script before then to issue a new certificate, or the nightly sync stops.',
          ],
          commands: [
            `pwsh -File .\\register-quickdial-sync-app.ps1 -PostTo ${v.apiOrigin}`,
          ],
        },
        { kind: 'link', text: 'Download register-quickdial-sync-app.ps1', href: v.registerScriptUrl },
        {
          kind: 'option',
          label: 'Prefer to do it by hand?',
          text: 'The script does only what you would do yourself. In the Microsoft Entra admin center of your own tenant:',
          items: [
            'App registrations → New registration (a name is all it needs). Its Overview page has the Application (client) ID.',
            'Create the certificate with the commands below. They write a .pfx holding the private key, for the machine that runs the task, and a .cer holding only the public half.',
            'That app → Certificates & secrets → Certificates → Upload certificate → pick the .cer.',
            'That app → API permissions → Add a permission → Microsoft Graph → Application permissions → Organization.Read.All → Add, then Grant admin consent. That single permission is enough: the export only uses Teams -Cs cmdlets.',
            'Roles and administrators → Teams Administrator → Add assignment → select the app.',
            'If the certificate is already in the user store of the account that runs the task, you can pass -CertificateThumbprint <thumbprint> instead of -CertificatePath; the script takes either.',
          ],
          commands: [
            `$pw = Read-Host "Choose a password for the .pfx" -AsSecureString\n$cert = New-SelfSignedCertificate -Subject "CN=Quick Dial sync" -CertStoreLocation Cert:\\CurrentUser\\My -KeySpec Signature -KeyExportPolicy Exportable -NotAfter (Get-Date).AddYears(2)\nExport-PfxCertificate -Cert $cert -FilePath ${PFX_PATH} -Password $pw\nExport-Certificate -Cert $cert -FilePath C:\\QuickDial\\quickdial-sync.cer`,
          ],
        },
        {
          kind: 'option',
          label: 'Option B — dedicated service account',
          text: 'A service account with the Teams Administrator role that is excluded from interactive MFA for this task, passed as -Credential instead of -ApplicationId and the certificate. It cannot drive a Windows scheduled task, so read this before choosing it:',
          items: [
            'Task Scheduler starts the script as pwsh -File, and -File passes everything after the script path as literal text: -Credential (Get-Credential) arrives as that text instead of asking for a password. A task set to "Run whether user is logged on or not" has no screen to ask on in any case. The script recognises that argument and stops with a message saying so, rather than failing at 02:00 with nothing to go on.',
            `Run it by hand: sign in to the machine as the service account, open PowerShell and start the script from the prompt — ./export-teams-callflows.ps1 -PostTo ${v.apiOrigin} -TenantId ${v.tenantId} -Credential (Get-Credential). At a prompt the expression is evaluated and you are asked for the password. A good way to test the account; not a way to run it nightly.`,
            "Azure Automation: store the account as an Automation credential and let the runbook fetch it — -Credential (Get-AutomationPSCredential -Name '<asset name>'). A runbook is a script rather than a command line, so the expression runs and the password never sits on a command line.",
            "One wrinkle on recent modules: from MicrosoftTeams 7.8.1-preview a -Credential sign-in on Windows goes through Web Account Manager, which Microsoft does not support in a RunAs context — an Automation sandbox is one. The script checks whether your module has Microsoft's -DisableWAM switch and passes it for you, so there is nothing to add anywhere. Microsoft calls that switch temporary, which is one more reason to build on Option A.",
            'For the nightly task on a Windows machine, use Option A. One script sets the whole thing up, and no one has to keep a service account out of MFA for it.',
          ],
        },
        { kind: 'p', text: 'Test the sign-in once by hand before scheduling anything. This reads the .pfx straight from disk, so it works from any account that can open the file:' },
        { kind: 'cmd', command: `$cert = Get-PfxCertificate -FilePath ${PFX_PATH}; Connect-MicrosoftTeams -ApplicationId <application-id> -Certificate $cert -TenantId ${v.tenantId}` },
        { kind: 'cmd', command: 'Get-CsAutoAttendant | Select-Object Name' },
      ],
    },
    {
      title: 'Give the task its two secrets',
      blocks: [
        { kind: 'p', text: `Generate the sync secret in this dialog. It and the .pfx password go into environment variables on the machine that runs the task. Set them machine-wide, from an elevated PowerShell: that way nobody has to sign in as the account the task runs as, and a 'User' variable would in any case land in your own profile rather than that account's.` },
        { kind: 'cmd', command: `[Environment]::SetEnvironmentVariable('${SECRET_ENV}', '<the secret>', 'Machine')` },
        { kind: 'cmd', command: `[Environment]::SetEnvironmentVariable('${CERT_PASSWORD_ENV}', '<the .pfx password>', 'Machine')` },
        { kind: 'p', text: `Machine-wide means any account on that server can read them, so keep the machine to this purpose. If you would rather store neither, pass the sync secret as -Secret and the password as -CertificatePassword in the task's arguments instead — but those are then visible to anyone who can read the task definition.` },
      ],
    },
    {
      title: 'Create the scheduled task',
      blocks: [
        {
          kind: 'option',
          label: 'Windows Task Scheduler',
          text: 'Create Task, then set:',
          items: [
            'General: "Run whether user is logged on or not".',
            'Triggers: Daily, for example 02:00.',
            'Actions: Start a program — Program "pwsh", Arguments as below. They are the certificate sign-in from Option A: a scheduled task has no way to supply a password.',
            'Settings: stop the task if it runs longer than 1 hour.',
          ],
        },
        {
          kind: 'option',
          label: 'Azure Automation',
          text: 'A PowerShell 7.2 runbook with the MicrosoftTeams module imported, the certificate stored as an Automation certificate, the secret as an encrypted variable, and a daily schedule. A runbook is the one unattended place where Option B also works, because it can fetch the credential itself.',
        },
        { kind: 'cmd', command: syncArguments(v) },
      ],
    },
    {
      title: 'Run it once by hand and check the result here',
      blocks: [
        { kind: 'p', text: 'Right-click the task → Run, then reopen this dialog: "Last run" shows what the sync did. The first run is always a dry run.' },
        {
          kind: 'rows',
          label: 'Exit codes of the script',
          items: [
            ['0', 'success'],
            ['1', 'a parameter or the environment is wrong — including a sign-in that failed, or a read that came back empty'],
            ['2', 'Quick Dial rejected the export (wrong secret, sync switched off, or a malformed file)'],
          ],
        },
      ],
    },
  ];
}

/** One block as lines of plain text, indented to sit under its step. */
function blockToText(b: StepBlock): string[] {
  switch (b.kind) {
    case 'p':
      return [`   ${b.text}`];
    case 'cmd':
      return b.command.split('\n').map(line => `   > ${line}`);
    case 'link':
      return [`   ${b.text}: ${b.href}`];
    case 'option':
      // Leading blank line so the alternatives stand apart in a plain-text ticket.
      return [
        '',
        `   ${b.label}${b.badge ? ` (${b.badge})` : ''}`,
        `   ${b.text}`,
        ...(b.items || []).map((it, i) => `      ${i + 1}. ${it}`),
        ...(b.commands || []).flatMap(c => c.split('\n').map(line => `      > ${line}`)),
        '',
      ];
    case 'rows':
      return [
        ...(b.label ? [`   ${b.label}:`] : []),
        ...b.items.map(([k, meaning]) => `      ${k} = ${meaning}`),
      ];
  }
}

/** Plain text an admin can paste into a ticket for their IT department. */
export function syncInstructionsText(v: SyncSetupValues): string {
  const lines: string[] = [];
  lines.push(`${v.appName || 'Quick Dial'} — nightly sync from Microsoft Teams: setup for IT`);
  lines.push('');
  lines.push('What it is: a scheduled task in our own Microsoft 365 organisation that exports the Teams auto attendants and call queues once a night (read-only) and sends them to Quick Dial, which keeps the phone-menu guide current.');
  lines.push('');
  lines.push(`Quick Dial API (the -PostTo value): ${v.apiOrigin}`);
  lines.push(`Tenant ID: ${v.tenantId}`);
  lines.push(`Script: ${v.scriptUrl}`);
  lines.push('');
  syncSetupSteps(v).forEach((s, i) => {
    lines.push(`${i + 1}. ${s.title}`);
    for (const b of s.blocks) lines.push(...blockToText(b));
    lines.push('');
  });
  lines.push('Full command line for the task:');
  lines.push(`   ${syncCommand(v)}`);
  lines.push('');
  lines.push('The secret is shown once in Quick Dial (Call flows → Sync → Generate secret); ask the Quick Dial admin for it, do not put it in this ticket.');
  // Blocks pad themselves, so collapse the runs of blank lines where two meet.
  return lines.join('\n').replace(/\n{3,}/g, '\n\n');
}
